type ScopeUser = {
  id?: string;
  roleId?: string;
  managedLocationIds?: string[] | null;
  requestLocationIds?: string[] | null;
  deliveryLocationIds?: string[] | null;
  locationIds?: string[] | null;
};

type ScopeRole = { id?: string; name?: string };
type ScopedLocation = { id: string; [key: string]: unknown };

export function isLocationScopeAdmin(user?: any, roles: ScopeRole[] = []) {
  const scopedUser = user as ScopeUser | null | undefined;
  const role = roles.find((candidate) => candidate.id === scopedUser?.roleId);
  return scopedUser?.roleId === "admin" ||
    scopedUser?.roleId === "system_admin" ||
    ["Admin", "Administrator", "System Admin"].includes(role?.name || "");
}

export function managedLocationIds(user?: any): string[] {
  const scopedUser = user as ScopeUser | null | undefined;
  return Array.from(new Set(scopedUser?.managedLocationIds || [])).sort();
}

export function scopedLocations<T extends ScopedLocation>(
  locations: T[],
  user?: any,
  roles: ScopeRole[] = [],
): T[] {
  if (isLocationScopeAdmin(user, roles)) return locations;
  const allowed = new Set(managedLocationIds(user));
  return locations.filter((location) => allowed.has(location.id));
}

export function orderLocationOptions<T extends ScopedLocation & { type?: string | null }>(
  locations: T[],
  user?: any,
  roles: ScopeRole[] = [],
): { sources: T[]; destinations: T[] } {
  if (isLocationScopeAdmin(user, roles)) {
    return {
      sources: locations,
      destinations: locations,
    };
  }

  const scopedUser = user as ScopeUser | null | undefined;
  const managed = new Set(managedLocationIds(scopedUser));
  const requestIds = scopedUser?.requestLocationIds?.filter(Boolean) || [];
  // An empty request assignment means unrestricted workspace sources. Legacy
  // locationIds describe the user's branch and must not be treated as the
  // location fulfilling that branch's orders.
  const sourceIds = requestIds.length ? new Set(requestIds) : null;

  return {
    sources: sourceIds ? locations.filter((location) => sourceIds.has(location.id)) : locations,
    destinations: locations.filter(
      (location) => managed.has(location.id),
    ),
  };
}

type OrderDefault = {
  locationId: string | null;
  source: "explicit" | "derived" | "none";
};

export function alignedOrderLocationDefaults(
  options: { sources: Array<{ id: string }>; destinations: Array<{ id: string }> },
  defaults?: { from: OrderDefault; to: OrderDefault } | null,
): { from: OrderDefault; to: OrderDefault } {
  const sourceIds = new Set(options.sources.map(({ id }) => id));
  const destinationIds = new Set(options.destinations.map(({ id }) => id));
  const validDefault = (
    side: OrderDefault | undefined,
    ids: Set<string>,
  ): OrderDefault | null => (
    side?.locationId && ids.has(side.locationId) ? side : null
  );

  let from = validDefault(defaults?.from, sourceIds);
  let to = validDefault(defaults?.to, destinationIds);
  if (!from && options.sources.length === 1) {
    from = { locationId: options.sources[0].id, source: "derived" };
  }
  if (!to && options.destinations.length === 1) {
    to = { locationId: options.destinations[0].id, source: "derived" };
  }
  if (from?.locationId && from.locationId === to?.locationId) {
    to = null;
  }
  return {
    from: from || { locationId: null, source: "none" },
    to: to || { locationId: null, source: "none" },
  };
}

export function alignedOperationalLocationSelections(
  options: {
    sources: Array<{ id: string }>;
    destinations: Array<{ id: string }>;
  },
  selections: {
    fromLocationId?: string | null;
    toLocationId?: string | null;
  },
): { fromLocationId: string; toLocationId: string } {
  const sourceIds = new Set(options.sources.map(({ id }) => id));
  const destinationIds = new Set(options.destinations.map(({ id }) => id));
  const fromLocationId = selections.fromLocationId &&
    sourceIds.has(selections.fromLocationId)
    ? selections.fromLocationId
    : "";
  const toLocationId = selections.toLocationId &&
    destinationIds.has(selections.toLocationId) &&
    selections.toLocationId !== fromLocationId
    ? selections.toLocationId
    : "";
  return { fromLocationId, toLocationId };
}

export function validLocationSelection(
  selection: string | null | undefined,
  allowedLocations: Array<{ id: string }>,
  allowAll = true,
) {
  if (!selection) return allowAll ? "all" : "";
  if (allowAll && selection === "all") return "all";
  return allowedLocations.some((location) => location.id === selection)
    ? selection
    : (allowAll ? "all" : "");
}

export function locationScopeKey(
  workspaceId?: string,
  userId?: string,
  locationId?: string,
  filters?: unknown,
) {
  return [workspaceId || "", userId || "", locationId || "all", filters ?? null] as const;
}